import React from 'react';
import { Brain, MessageSquare, Sparkles } from 'lucide-react';

interface EmptyStateProps {
  onCreateTopic: () => void;
  hasTopics: boolean;
  isSearching?: boolean;
  variant?: 'main' | 'topicList'; // Specify if it's used in main content or topic list
  hideHeader?: boolean; // Option to hide header for topic list usage
}

export function EmptyState({
  onCreateTopic,
  hasTopics,
  isSearching = false,
  variant = 'main',
  hideHeader = false,
}: EmptyStateProps) {
  // Adjust sizing based on variant
  const iconSize = variant === 'topicList' ? 'w-20 h-20' : 'w-24 h-24';
  const iconInnerSize = variant === 'topicList' ? 'w-10 h-10' : 'w-12 h-12';
  const decorSize = variant === 'topicList' ? 'w-7 h-7' : 'w-8 h-8';
  const decorIconSize = variant === 'topicList' ? 'w-3.5 h-3.5' : 'w-4 h-4';
  const paddingClass = variant === 'topicList' ? 'p-6' : 'p-8';
  const maxWidth = variant === 'topicList' ? 'max-w-sm' : 'max-w-md';

  return (
    <div className={`h-full flex items-center justify-center ${paddingClass}`}>
      {/* Add white container with shadow for main variant only */}
      <div
        className={`${maxWidth} text-center ${
          variant === 'main' ? 'bg-white rounded-2xl shadow-xl p-8 py-10' : ''
        }`}
      >
        <div className="relative mb-8">
          {/* Main icon with glow effect */}
          <div className={`relative ${iconSize} mx-auto`}>
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl opacity-75 blur-lg transform transition-all duration-200 group-hover:scale-110" />
            {/* Main icon */}
            <div
              className={`relative ${iconSize} bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-[0_8px_16px_rgba(59,91,219,0.2)] transform transition-all duration-200 hover:scale-110 hover:shadow-[0_12px_24px_rgba(59,91,219,0.3)]`}
            >
              <Brain className={`${iconInnerSize} text-white`} />
            </div>
            {/* Decorative icons - positioned closer to main icon */}
            <div
              className={`absolute -top-2 -right-2 ${decorSize} bg-yellow-100 rounded-lg flex items-center justify-center transform rotate-12 shadow-sm animate-pulse-subtle`}
            >
              <Sparkles className={`${decorIconSize} text-yellow-600`} />
            </div>
            <div
              className={`absolute -bottom-2 -left-2 ${decorSize} bg-green-100 rounded-lg flex items-center justify-center transform -rotate-12 shadow-sm animate-float`}
            >
              <MessageSquare className={`${decorIconSize} text-green-600`} />
            </div>
          </div>
        </div>

        {!hideHeader && (
          <>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-sora">
              {isSearching
                ? 'No Topics Found'
                : hasTopics
                  ? 'Select or Create a Topic'
                  : 'Start Your First Topic'}
            </h2>

            <p className="text-gray-600 mb-8 text-lg leading-relaxed">
              {isSearching
                ? 'No topics match your search criteria. Try adjusting your search terms.'
                : hasTopics
                  ? 'Choose a topic from the sidebar to start working, or create a new one to begin a fresh space.'
                  : 'Create your first topic to start organizing your project.'}
            </p>
          </>
        )}

        <button
          onClick={onCreateTopic}
          className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl px-6 py-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:translate-y-[-2px] animated-button"
        >
          <MessageSquare className="w-6 h-6" />
          <span className="text-lg font-semibold">New Topic</span>
        </button>
      </div>
    </div>
  );
}
